import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_travel-fulfillment/artifacts/sj7p681s_knockout.png";
const HERO_IMAGE_URL = "https://images.unsplash.com/photo-1767170549468-2304b1b46e9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxjYWxtJTIwc29saXRhcnklMjB0cmF2ZWxlciUyMG5hdHVyZXxlbnwwfHx8fDE3Njk3ODIxMDJ8MA&ixlib=rb-4.1.0&q=85";
const EMAIL = "turnerexcursions@gmail.com";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Brand Colors
const COLORS = {
  navy: '#0B1F3B',
  deepBlue: '#1036DD',
  slateBlue: '#5C6F91',
  charcoal: '#2E2E2E',
  lightGray: '#BFBFBF',
  offWhite: '#F5F7FA',
};

// ============================================
// QUIZ DATA
// ============================================
const QUIZ_DATA = {
  questions: [
    {
      id: 1,
      question: "When you travel, you're usually the one who…",
      answers: [
        { key: "A", text: "Finds the best flights and hotels", score: 1 },
        { key: "B", text: "Plans the whole trip for yourself (and sometimes others)", score: 2 },
        { key: "C", text: "Handles logistics and details naturally", score: 3 },
        { key: "D", text: "Just enjoys the experience once it's booked", score: 0 },
      ]
    },
    {
      id: 2,
      question: "Friends or family often say this about you:",
      answers: [
        { key: "A", text: '"You\'re great at finding deals"', score: 1 },
        { key: "B", text: '"You should plan trips professionally"', score: 2 },
        { key: "C", text: '"You always know how to make travel smooth"', score: 3 },
        { key: "D", text: '"You travel a lot — I wish I could do that"', score: 0 },
      ]
    },
    {
      id: 3,
      question: "How do you feel about travel planning?",
      answers: [
        { key: "A", text: "I enjoy it when it's for me", score: 1 },
        { key: "B", text: "I enjoy it and don't mind helping others", score: 2 },
        { key: "C", text: "I like structure, systems, and making things efficient", score: 3 },
        { key: "D", text: "I'd rather not deal with planning at all", score: 0 },
      ]
    },
    {
      id: 4,
      question: "When you hear 'travel business,' your first thought is:",
      answers: [
        { key: "A", text: '"Sounds interesting, but I\'ve never considered it"', score: 1 },
        { key: "B", text: '"I could see myself doing this casually"', score: 2 },
        { key: "C", text: '"I want to understand how this could actually scale"', score: 3 },
        { key: "D", text: '"That\'s not really what I\'m looking for"', score: 0 },
      ]
    },
    {
      id: 5,
      question: "What motivates you most right now?",
      answers: [
        { key: "A", text: "Traveling smarter and more often", score: 1 },
        { key: "B", text: "Helping others travel better", score: 2 },
        { key: "C", text: "Creating more income flexibility", score: 3 },
        { key: "D", text: "Enjoying life without added responsibility", score: 0 },
      ]
    },
    {
      id: 6,
      question: "How do you feel about earning from something you already do?",
      answers: [
        { key: "A", text: "Curious, but cautious", score: 1 },
        { key: "B", text: "Open if it feels natural", score: 2 },
        { key: "C", text: "Very interested if it's structured", score: 3 },
        { key: "D", text: "Not interested — I just enjoy travel", score: 0 },
      ]
    },
    {
      id: 7,
      question: "Which statement feels most true?",
      answers: [
        { key: "A", text: '"Travel is my lifestyle"', score: 1 },
        { key: "B", text: '"I\'d love to help others experience travel"', score: 2 },
        { key: "C", text: '"I want ownership over my time and income"', score: 3 },
        { key: "D", text: '"I travel purely for enjoyment"', score: 0 },
      ]
    },
    {
      id: 8,
      question: "Ideally, travel fits into your life as:",
      answers: [
        { key: "A", text: "A personal passion", score: 1 },
        { key: "B", text: "A way to help friends and family", score: 2 },
        { key: "C", text: "A platform for opportunity", score: 3 },
        { key: "D", text: "A reward, not a responsibility", score: 0 },
      ]
    }
  ],
  results: [
    {
      minScore: 0,
      maxScore: 8,
      persona: "Lifestyle Traveler",
      route: "/lifestyle",
      message: "Your answers show that travel currently serves you best as a lifestyle and personal joy — not a business."
    },
    {
      minScore: 9,
      maxScore: 14,
      persona: "Connector",
      route: "/connector",
      message: "Your answers suggest you naturally connect people and enjoy helping others, with opportunities staying flexible and optional."
    },
    {
      minScore: 15,
      maxScore: 19,
      persona: "Strategic Explorer",
      route: "/strategic",
      message: "Your responses indicate a desire to understand how systems work before making decisions — clarity comes first for you."
    },
    {
      minScore: 20,
      maxScore: 24,
      persona: "Ownership / Builder",
      route: "/ownership",
      message: "Your answers reflect a mindset oriented toward ownership, leverage, and building something with long-term control."
    }
  ]
};

// ============================================
// QUIZ PAGE - Travel Mindset Navigator
// ============================================
const QuizPage = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [contactInfo, setContactInfo] = useState({ firstName: '', email: '', contactId: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const calculateResult = () => {
    const totalScore = Object.values(answers).reduce((sum, answer) => sum + answer.score, 0);
    
    const matchedResult = QUIZ_DATA.results.find(
      r => totalScore >= r.minScore && totalScore <= r.maxScore
    );
    
    return matchedResult;
  };

  const saveResultAndSendEmail = async (contactId, resultData) => {
    try {
      await fetch(`${BACKEND_URL}/api/assessment/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: contactId,
          result: resultData.persona
        })
      });
    } catch (error) {
      console.error('Error saving result:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactInfo.firstName || !contactInfo.email) return;
    
    setIsSubmitting(true);
    try {
      // Create contact
      const response = await fetch(`${BACKEND_URL}/api/assessment/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: contactInfo.firstName,
          email: contactInfo.email
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const contactId = data.id;
        
        // Calculate result
        const finalResult = calculateResult();
        setResult(finalResult);
        
        // Save result and trigger email
        await saveResultAndSendEmail(contactId, finalResult);
        
        // Show transition then result
        setShowContactForm(false);
        setShowTransition(true);
        
        setTimeout(() => {
          setShowTransition(false);
          setShowResult(true);
        }, 2500);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_DATA.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Show contact form after last question
      setShowContactForm(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleViewResult = () => {
    if (result) {
      navigate(result.route);
    }
  };

  const handleStartQuiz = () => {
    setShowIntro(false);
  };

  const currentQ = QUIZ_DATA.questions[currentQuestion];
  const selectedAnswer = answers[currentQ?.id];
  const progress = ((currentQuestion + 1) / QUIZ_DATA.questions.length) * 100;

  // CONTACT FORM PAGE (Before showing result)
  if (showContactForm) {
    return (
      <div 
        data-testid="quiz-contact-page" 
        className="min-h-screen flex flex-col items-center"
        style={{ backgroundColor: COLORS.offWhite }}
      >
        <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
          {/* Logo */}
          <section className="flex justify-center mb-8 opacity-0 animate-fade-in-up">
            <img 
              src={LOGO_URL} 
              alt="Turner Excursions Logo" 
              className="logo-image"
              data-testid="quiz-contact-logo"
            />
          </section>

          {/* Title Section */}
          <section className="text-center mb-8 opacity-0 animate-fade-in-up animate-delay-100">
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-wide mb-4"
              style={{ color: COLORS.navy }}
            >
              Almost there — where should I send your result?
            </h1>
            <p 
              className="text-base md:text-lg"
              style={{ color: COLORS.slateBlue }}
            >
              Enter your details below to see your Travel Mindset result + personalized next steps.
            </p>
          </section>

          {/* Divider */}
          <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

          {/* Contact Form */}
          <section className="opacity-0 animate-fade-in-up animate-delay-300">
            <form onSubmit={handleContactSubmit} className="max-w-md mx-auto space-y-6">
              <div>
                <label 
                  htmlFor="firstName" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: COLORS.charcoal }}
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={contactInfo.firstName}
                  onChange={(e) => setContactInfo(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-4 py-3 text-base rounded-lg focus:outline-none focus:ring-2 transition-all duration-300"
                  style={{ 
                    backgroundColor: 'white',
                    border: `1px solid ${COLORS.lightGray}`,
                    color: COLORS.charcoal,
                  }}
                  placeholder="Enter your first name"
                  required
                  data-testid="contact-first-name"
                />
              </div>
              
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: COLORS.charcoal }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 text-base rounded-lg focus:outline-none focus:ring-2 transition-all duration-300"
                  style={{ 
                    backgroundColor: 'white',
                    border: `1px solid ${COLORS.lightGray}`,
                    color: COLORS.charcoal,
                  }}
                  placeholder="Enter your email address"
                  required
                  data-testid="contact-email"
                />
              </div>

              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || !contactInfo.firstName || !contactInfo.email}
                  className="primary-cta-button disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Please wait...' : 'See My Result'}
                </button>
              </div>
            </form>
          </section>

          {/* Footer */}
          <footer className="text-center pt-16 mt-8">
            <p 
              className="text-xs tracking-widest uppercase"
              style={{ color: COLORS.lightGray }}
            >
              Travel. Access. Opportunity.
            </p>
          </footer>
        </main>
      </div>
    );
  }

  // INTRO PAGE
  if (showIntro) {
    return (
      <div 
        data-testid="quiz-intro-page" 
        className="min-h-screen flex flex-col items-center"
        style={{ backgroundColor: COLORS.offWhite }}
      >
        <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
          {/* Logo */}
          <section className="flex justify-center mb-12 opacity-0 animate-fade-in-up">
            <img 
              src={LOGO_URL} 
              alt="Turner Excursions Logo" 
              className="logo-image"
              data-testid="quiz-intro-logo"
            />
          </section>

          {/* Title Section */}
          <section className="text-center mb-8 opacity-0 animate-fade-in-up animate-delay-100">
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-4 uppercase"
              style={{ color: COLORS.navy }}
              data-testid="quiz-intro-title"
            >
              Travel Mindset Navigator
            </h1>
            <p 
              className="text-sm tracking-wide mb-4"
              style={{ color: COLORS.slateBlue }}
            >
              Lifestyle • Connection • Intentional Opportunity
            </p>
            <p 
              className="text-lg md:text-xl font-medium"
              style={{ color: COLORS.charcoal }}
            >
              Discover how travel fits into your life right now
            </p>
          </section>

          {/* Divider */}
          <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

          {/* Description */}
          <section className="text-center mb-10 opacity-0 animate-fade-in-up animate-delay-300">
            <p 
              className="text-base md:text-lg leading-relaxed mb-6"
              style={{ color: COLORS.charcoal }}
            >
              This short assessment helps you identify whether travel fits you best as a lifestyle, a way to connect and help others, or a platform worth exploring more intentionally — all at your own pace.
            </p>
            <p 
              className="text-base md:text-lg font-medium"
              style={{ color: COLORS.charcoal }}
            >
              No pressure. No selling. Just clarity.
            </p>
          </section>

          {/* Start Button */}
          <section className="text-center mb-8 opacity-0 animate-fade-in-up animate-delay-400">
            <button
              onClick={handleStartQuiz}
              className="primary-cta-button"
              data-testid="quiz-start-button"
            >
              Start Assessment
            </button>
          </section>

          {/* Helper Text */}
          <section className="text-center opacity-0 animate-fade-in-up animate-delay-500">
            <p 
              className="text-xs"
              style={{ color: COLORS.lightGray }}
            >
              Takes about 2 minutes.
            </p>
          </section>

          {/* Footer */}
          <footer className="text-center pt-16 mt-8">
            <p 
              className="text-xs tracking-widest uppercase"
              style={{ color: COLORS.lightGray }}
            >
              Travel. Access. Opportunity.
            </p>
          </footer>
        </main>
      </div>
    );
  }

  // TRANSITION PAGE (after last question, before results)
  if (showTransition) {
    return (
      <div 
        data-testid="quiz-transition-page" 
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: COLORS.offWhite }}
      >
        <main className="w-full max-w-[720px] px-6 text-center">
          <p 
            className="text-lg md:text-xl leading-relaxed opacity-0 animate-fade-in-up"
            style={{ color: COLORS.charcoal }}
          >
            Your result reflects where travel fits you right now — not a permanent label.
          </p>
          <div className="mt-8 opacity-0 animate-fade-in-up animate-delay-200">
            <div 
              className="inline-block w-8 h-8 border-2 rounded-full animate-spin"
              style={{ borderColor: COLORS.lightGray, borderTopColor: COLORS.deepBlue }}
            />
          </div>
        </main>
      </div>
    );
  }

  // RESULT PAGE
  if (showResult && result) {
    return (
      <div 
        data-testid="quiz-result-page" 
        className="min-h-screen flex flex-col items-center"
        style={{ backgroundColor: COLORS.offWhite }}
      >
        <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
          {/* Logo */}
          <section className="flex justify-center mb-16 opacity-0 animate-fade-in-up">
            <img 
              src={LOGO_URL} 
              alt="Turner Excursions Logo" 
              className="logo-image"
              data-testid="quiz-result-logo"
            />
          </section>

          {/* Result Card */}
          <section 
            className="text-center opacity-0 animate-fade-in-up animate-delay-100"
            data-testid="quiz-result-section"
          >
            <p 
              className="text-sm uppercase tracking-widest mb-4"
              style={{ color: COLORS.slateBlue }}
            >
              Your Result
            </p>
            
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-8 uppercase"
              style={{ color: COLORS.navy }}
              data-testid="quiz-result-persona"
            >
              {result.persona}
            </h1>

            <p 
              className="text-lg md:text-xl leading-relaxed mb-12"
              style={{ color: COLORS.charcoal }}
              data-testid="quiz-result-message"
            >
              {result.message}
            </p>

            <button
              onClick={handleViewResult}
              className="primary-cta-button"
              data-testid="quiz-view-result-button"
            >
              See What This Means For You
            </button>
          </section>

          {/* Footer */}
          <footer className="text-center pt-16 mt-16" style={{ borderTop: `1px solid ${COLORS.lightGray}` }}>
            <p 
              className="text-xs tracking-widest uppercase"
              style={{ color: COLORS.lightGray }}
            >
              Travel. Access. Opportunity.
            </p>
          </footer>
        </main>
      </div>
    );
  }

  return (
    <div 
      data-testid="quiz-page" 
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.offWhite }}
    >
      <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
        {/* Logo */}
        <section className="flex justify-center mb-12 opacity-0 animate-fade-in-up">
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions Logo" 
            className="h-16 w-auto object-contain"
            data-testid="quiz-logo"
          />
        </section>

        {/* Quiz Title */}
        <section className="text-center mb-8 opacity-0 animate-fade-in-up animate-delay-100">
          <h1 
            className="text-2xl sm:text-3xl font-bold tracking-wide mb-2 uppercase"
            style={{ color: COLORS.navy }}
          >
            Travel Mindset Navigator
          </h1>
          <p 
            className="text-sm"
            style={{ color: COLORS.slateBlue }}
          >
            Discover how travel fits into your life right now
          </p>
        </section>

        {/* Progress Bar */}
        <section className="mb-8 opacity-0 animate-fade-in-up animate-delay-200">
          <div className="flex justify-between text-xs mb-2" style={{ color: COLORS.slateBlue }}>
            <span>Question {currentQuestion + 1} of {QUIZ_DATA.questions.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div 
            className="h-1 rounded-full overflow-hidden"
            style={{ backgroundColor: COLORS.lightGray }}
          >
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${progress}%`,
                backgroundColor: COLORS.deepBlue 
              }}
              data-testid="quiz-progress-bar"
            />
          </div>
        </section>

        {/* Pre-Question Reassurance (only on Q1) */}
        {currentQuestion === 0 && (
          <section className="text-center mb-8 opacity-0 animate-fade-in-up animate-delay-200">
            <p 
              className="text-sm leading-relaxed"
              style={{ color: COLORS.slateBlue }}
            >
              There are no right or wrong answers.
              <br />
              Choose what feels most true for you right now.
            </p>
          </section>
        )}

        {/* Question */}
        <section className="mb-8 opacity-0 animate-fade-in-up animate-delay-300">
          <h2 
            className="text-xl md:text-2xl font-medium text-center mb-8"
            style={{ color: COLORS.charcoal }}
            data-testid="quiz-question"
          >
            {currentQ.question}
          </h2>

          {/* Answer Options */}
          <div className="space-y-4">
            {currentQ.answers.map((answer) => (
              <button
                key={answer.key}
                onClick={() => handleAnswerSelect(currentQ.id, answer)}
                className={`w-full p-5 text-left rounded-lg transition-all duration-300 ${
                  selectedAnswer?.key === answer.key 
                    ? 'ring-2' 
                    : 'hover:shadow-md'
                }`}
                style={{
                  backgroundColor: selectedAnswer?.key === answer.key ? 'white' : 'white',
                  borderColor: selectedAnswer?.key === answer.key ? COLORS.deepBlue : COLORS.lightGray,
                  border: `1px solid ${selectedAnswer?.key === answer.key ? COLORS.deepBlue : COLORS.lightGray}`,
                  ringColor: COLORS.deepBlue,
                }}
                data-testid={`quiz-answer-${answer.key}`}
              >
                <div className="flex items-start gap-4">
                  <span 
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                    style={{
                      backgroundColor: selectedAnswer?.key === answer.key ? COLORS.deepBlue : COLORS.offWhite,
                      color: selectedAnswer?.key === answer.key ? 'white' : COLORS.slateBlue,
                    }}
                  >
                    {answer.key}
                  </span>
                  <span 
                    className="text-base"
                    style={{ color: COLORS.charcoal }}
                  >
                    {answer.text}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Navigation */}
        <section className="flex justify-between items-center pt-8 opacity-0 animate-fade-in-up animate-delay-400">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="px-6 py-3 text-sm font-medium transition-opacity duration-300"
            style={{ 
              color: currentQuestion === 0 ? COLORS.lightGray : COLORS.slateBlue,
              cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer'
            }}
            data-testid="quiz-prev-button"
          >
            ← Back
          </button>

          <button
            onClick={handleNext}
            disabled={!selectedAnswer}
            className="primary-cta-button disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="quiz-next-button"
          >
            {currentQuestion === QUIZ_DATA.questions.length - 1 ? 'See My Result' : 'Continue'}
          </button>
        </section>

        {/* Footer */}
        <footer className="text-center pt-16 mt-8">
          <p 
            className="text-xs tracking-widest uppercase"
            style={{ color: COLORS.lightGray }}
          >
            Travel. Access. Opportunity.
          </p>
        </footer>
      </main>
    </div>
  );
};

// ============================================
// OWNERSHIP PAGE - "You're Built For Ownership"
// ============================================
const OwnershipPage = () => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div 
      data-testid="ownership-page" 
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.offWhite }}
    >
      {/* Main Content Container */}
      <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
        
        {/* Logo Section */}
        <section 
          data-testid="ownership-logo-section"
          className="flex justify-center mb-16 opacity-0 animate-fade-in-up"
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions Logo" 
            className="logo-image"
            data-testid="ownership-logo-image"
          />
        </section>

        {/* Hero Section */}
        <section 
          data-testid="ownership-hero-section"
          className="text-center mb-12 opacity-0 animate-fade-in-up animate-delay-100"
        >
          {/* Headline */}
          <h1 
            data-testid="ownership-headline"
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-4 leading-tight uppercase"
            style={{ color: COLORS.navy }}
          >
            You're Built For Ownership
          </h1>

          {/* Sub-headline */}
          <p 
            data-testid="ownership-subheadline"
            className="text-xl md:text-2xl font-medium mb-12"
            style={{ color: COLORS.charcoal }}
          >
            Travel isn't just the product — it's the vehicle.
          </p>

          {/* Hero Image */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_travel-fulfillment/artifacts/yxge5fnu_IMG_8292.jpeg" 
              alt="Premium travel scenery" 
              className="hero-image"
              data-testid="ownership-hero-image"
            />
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

        {/* Body Copy Section */}
        <section 
          data-testid="ownership-body-section"
          className="mb-12 opacity-0 animate-fade-in-up animate-delay-300"
        >
          <div className="text-center space-y-8">
            <p 
              data-testid="ownership-body-1"
              className="text-lg md:text-xl leading-relaxed font-medium"
              style={{ color: COLORS.slateBlue }}
            >
              You value control over your time, income, and direction.
            </p>

            <p 
              data-testid="ownership-body-2"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              You're not drawn to hype or shortcuts.
              <br />
              You're looking for systems, transparency, and leverage.
            </p>

            <p 
              data-testid="ownership-body-3"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              For you, travel represents access to something bigger — ownership, flexibility, and long-term opportunity.
            </p>

            <p 
              data-testid="ownership-body-4"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              This path isn't for everyone.
              <br />
              It's for people who want ownership.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-400"></div>

        {/* Primary CTA Section */}
        <section 
          data-testid="ownership-cta-section"
          className="text-center mb-12 opacity-0 animate-fade-in-up animate-delay-500"
        >
          {!showVideo ? (
            <button
              onClick={() => setShowVideo(true)}
              className="primary-cta-button"
              data-testid="ownership-primary-cta-button"
            >
              Explore The Full Overview
            </button>
          ) : (
            <div 
              data-testid="ownership-video"
              className="relative w-full max-w-2xl mx-auto rounded overflow-hidden shadow-lg"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://player.vimeo.com/video/1012841798?h=0&title=0&byline=0&portrait=0&autoplay=1"
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Full Business Overview"
              ></iframe>
            </div>
          )}
        </section>

        {/* Secondary CTA Section */}
        <section 
          data-testid="ownership-secondary-section"
          className="text-center mb-16 opacity-0 animate-fade-in-up animate-delay-500"
        >
          <p 
            className="text-base mb-4"
            style={{ color: COLORS.charcoal }}
          >
            Finished the video? Let's get your questions answered.
          </p>
          <a 
            href="https://calendly.com/turnerexcursions/travel"
            target="_blank"
            rel="noopener noreferrer"
            className="secondary-cta-link"
            data-testid="ownership-secondary-link"
          >
            Schedule a call →
          </a>
        </section>

        {/* Footer */}
        <footer 
          data-testid="ownership-footer-section"
          className="text-center pt-8"
          style={{ borderTop: `1px solid ${COLORS.lightGray}` }}
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions" 
            className="h-10 w-auto mx-auto opacity-40 mb-4"
            data-testid="ownership-footer-logo"
          />
          <p 
            data-testid="ownership-footer-tagline"
            className="text-xs tracking-widest uppercase"
            style={{ color: COLORS.lightGray }}
          >
            Travel. Access. Opportunity.
          </p>
        </footer>

      </main>
    </div>
  );
};

// ============================================
// STRATEGIC EXPLORER PAGE - "The Strategic Explorer"
// ============================================
const PatternPage = () => {
  const [showPrimaryVideo, setShowPrimaryVideo] = useState(false);
  const [showSecondaryVideo, setShowSecondaryVideo] = useState(false);

  return (
    <div 
      data-testid="pattern-page" 
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.offWhite }}
    >
      {/* Main Content Container */}
      <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
        
        {/* Logo Section */}
        <section 
          data-testid="pattern-logo-section"
          className="flex justify-center mb-16 opacity-0 animate-fade-in-up"
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions Logo" 
            className="logo-image"
            data-testid="pattern-logo-image"
          />
        </section>

        {/* Hero Section */}
        <section 
          data-testid="pattern-hero-section"
          className="text-center mb-12 opacity-0 animate-fade-in-up animate-delay-100"
        >
          {/* Headline */}
          <h1 
            data-testid="pattern-headline"
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-4 leading-tight uppercase"
            style={{ color: COLORS.navy }}
          >
            The Strategic Explorer
          </h1>

          {/* Sub-headline */}
          <p 
            data-testid="pattern-subheadline"
            className="text-base md:text-lg font-medium mb-12"
            style={{ color: COLORS.slateBlue }}
          >
            Travel can be a platform — not just a passion.
          </p>

          {/* Hero Image */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_travel-fulfillment/artifacts/xgh9d1gt_69054DFE-2BBD-4DD8-AA03-D13D11545990.png" 
              alt="Strategic travel planning" 
              className="hero-image"
              data-testid="pattern-hero-image"
            />
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

        {/* Body Copy Section */}
        <section 
          data-testid="pattern-body-section"
          className="mb-12 opacity-0 animate-fade-in-up animate-delay-300"
        >
          <div className="text-center space-y-8">
            <p 
              data-testid="pattern-body-1"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              You don't just enjoy travel.
              <br />
              You notice how things work.
            </p>

            <div data-testid="pattern-body-2">
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: COLORS.charcoal }}
              >
                You're curious about:
              </p>
              <ul 
                className="text-lg md:text-xl leading-relaxed space-y-2"
                style={{ color: COLORS.charcoal }}
              >
                <li>• How the travel industry already pays commissions</li>
                <li>• How structure creates leverage</li>
                <li>• How time and effort can work together</li>
              </ul>
            </div>

            <p 
              data-testid="pattern-body-3"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              This isn't about selling trips nonstop.
            </p>

            <div data-testid="pattern-body-4">
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: COLORS.charcoal }}
              >
                It's about understanding:
              </p>
              <ul 
                className="text-lg md:text-xl leading-relaxed space-y-2"
                style={{ color: COLORS.charcoal }}
              >
                <li>• Where income actually comes from</li>
                <li>• How systems replace guesswork</li>
                <li>• What real involvement looks like</li>
              </ul>
            </div>

            <p 
              data-testid="pattern-body-5"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              No buzzwords.
              <br />
              No promises.
              <br />
              Just clarity.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-400"></div>

        {/* Primary CTA Section */}
        <section 
          data-testid="pattern-cta-section"
          className="text-center mb-12 opacity-0 animate-fade-in-up animate-delay-500"
        >
          {!showPrimaryVideo ? (
            <button
              onClick={() => setShowPrimaryVideo(true)}
              className="primary-cta-button"
              data-testid="pattern-primary-cta-button"
            >
              Understand The Travel Model
            </button>
          ) : (
            <div 
              data-testid="pattern-video"
              className="relative w-full max-w-2xl mx-auto rounded overflow-hidden shadow-lg"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://player.vimeo.com/video/324374319?h=0&title=0&byline=0&portrait=0&autoplay=1"
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="The Travel Model"
              ></iframe>
            </div>
          )}
        </section>

        {/* Secondary CTA Section */}
        <section 
          data-testid="pattern-secondary-section"
          className="text-center mb-16 opacity-0 animate-fade-in-up animate-delay-500"
        >
          <p 
            className="text-base mb-4"
            style={{ color: COLORS.charcoal }}
          >
            Curious how people build leverage beyond travel?
          </p>
          {!showSecondaryVideo ? (
            <button
              onClick={() => setShowSecondaryVideo(true)}
              className="secondary-cta-button"
              data-testid="pattern-secondary-cta-button"
            >
              → Explore the marketing side
            </button>
          ) : (
            <div 
              data-testid="pattern-secondary-video"
              className="relative w-full max-w-2xl mx-auto rounded overflow-hidden shadow-lg mt-6"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://player.vimeo.com/video/324403334?h=0&title=0&byline=0&portrait=0&autoplay=1"
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="The Marketing Side"
              ></iframe>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer 
          data-testid="pattern-footer-section"
          className="text-center pt-8"
          style={{ borderTop: `1px solid ${COLORS.lightGray}` }}
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions" 
            className="h-10 w-auto mx-auto opacity-40 mb-4"
            data-testid="pattern-footer-logo"
          />
          <p 
            data-testid="pattern-footer-tagline"
            className="text-xs tracking-widest uppercase"
            style={{ color: COLORS.lightGray }}
          >
            Travel. Access. Opportunity.
          </p>
        </footer>

      </main>
    </div>
  );
};

// ============================================
// CONNECTOR PAGE - "You're A Natural Connector"
// ============================================
const ConnectorPage = () => {
  const [showPrimaryVideo, setShowPrimaryVideo] = useState(false);
  const [showSecondaryVideo, setShowSecondaryVideo] = useState(false);

  return (
    <div 
      data-testid="connector-page" 
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.offWhite }}
    >
      {/* Main Content Container */}
      <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
        
        {/* Logo Section */}
        <section 
          data-testid="connector-logo-section"
          className="flex justify-center mb-16 opacity-0 animate-fade-in-up"
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions Logo" 
            className="logo-image"
            data-testid="connector-logo-image"
          />
        </section>

        {/* Hero Section */}
        <section 
          data-testid="connector-hero-section"
          className="text-center mb-12 opacity-0 animate-fade-in-up animate-delay-100"
        >
          {/* Headline */}
          <h1 
            data-testid="connector-headline"
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-6 leading-tight uppercase"
            style={{ color: COLORS.navy }}
          >
            You're A Natural Connector
          </h1>
          
          {/* Subhead */}
          <p 
            data-testid="connector-subhead"
            className="text-base md:text-lg font-medium mb-12"
            style={{ color: COLORS.slateBlue }}
          >
            You already help people travel — sometimes without realizing it.
          </p>

          {/* Hero Image */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_travel-fulfillment/artifacts/w5i4mifa_IMG_8299.jpeg" 
              alt="Friends enjoying travel together" 
              className="hero-image"
              data-testid="connector-hero-image"
            />
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

        {/* Body Copy Section */}
        <section 
          data-testid="connector-body-section"
          className="mb-12 opacity-0 animate-fade-in-up animate-delay-300"
        >
          <div className="text-center space-y-8">
            <p 
              data-testid="connector-body-1"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              People trust you.
              <br />
              They ask where to go, what to book, how to make it easier.
            </p>

            <p 
              data-testid="connector-body-2"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              For many, that's where it stays.
            </p>

            <p 
              data-testid="connector-body-3"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              But some choose to add structure —
              <br />
              not pressure, not selling — just a system that supports what they already do.
            </p>

            <p 
              data-testid="connector-body-4"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              This isn't about becoming a full-time business owner.
            </p>

            <p 
              data-testid="connector-body-5"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              It's about understanding how helping people travel can stay:
            </p>

            <ul 
              data-testid="connector-benefits-list"
              className="text-lg md:text-xl leading-relaxed space-y-2"
              style={{ color: COLORS.charcoal }}
            >
              <li>• Simple</li>
              <li>• Optional</li>
              <li>• Aligned with your life</li>
            </ul>
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-400"></div>

        {/* Primary CTA Section */}
        <section 
          data-testid="connector-primary-cta-section"
          className="text-center mb-16 opacity-0 animate-fade-in-up animate-delay-500"
        >
          {!showPrimaryVideo ? (
            <button
              onClick={() => setShowPrimaryVideo(true)}
              className="primary-cta-button"
              data-testid="connector-primary-cta-button"
            >
              Learn How This Could Work
            </button>
          ) : (
            <div 
              data-testid="connector-primary-video"
              className="relative w-full max-w-2xl mx-auto rounded overflow-hidden shadow-lg"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://player.vimeo.com/video/324401853?h=0&title=0&byline=0&portrait=0&autoplay=1"
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="How This Could Work"
              ></iframe>
            </div>
          )}
        </section>

        {/* Secondary CTA Section */}
        <section 
          data-testid="connector-secondary-cta-section"
          className="text-center mb-16 opacity-0 animate-fade-in-up animate-delay-500"
        >
          {!showSecondaryVideo ? (
            <button
              onClick={() => setShowSecondaryVideo(true)}
              className="secondary-cta-button"
              data-testid="connector-secondary-cta-button"
            >
              → Explore exactly how travel works
            </button>
          ) : (
            <div 
              data-testid="connector-secondary-video"
              className="relative w-full max-w-2xl mx-auto rounded overflow-hidden shadow-lg mt-6"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://player.vimeo.com/video/324374319?h=0&title=0&byline=0&portrait=0&autoplay=1"
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="How the Travel Side Works"
              ></iframe>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer 
          data-testid="connector-footer-section"
          className="text-center pt-8"
          style={{ borderTop: `1px solid ${COLORS.lightGray}` }}
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions" 
            className="h-10 w-auto mx-auto opacity-40 mb-4"
            data-testid="connector-footer-logo"
          />
          <p 
            data-testid="connector-footer-tagline"
            className="text-xs tracking-widest uppercase"
            style={{ color: COLORS.lightGray }}
          >
            Travel. Access. Opportunity.
          </p>
        </footer>

      </main>
    </div>
  );
};

// ============================================
// LIFESTYLE PAGE - "Travel Is Your Lifestyle"
// ============================================
const LandingPage = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSubscribed(true);
      setEmail("");
    } catch (error) {
      console.error("Subscription error:", error);
      setSubscribed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      data-testid="landing-page" 
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.offWhite }}
    >
      {/* Main Content Container */}
      <main className="w-full max-w-[720px] px-6 py-16 md:py-24">
        
        {/* Logo Section */}
        <section 
          data-testid="logo-section"
          className="flex justify-center mb-16 opacity-0 animate-fade-in-up"
        >
          <img 
            src={LOGO_URL} 
            alt="Turner Excursions Logo" 
            className="logo-image"
            data-testid="logo-image"
          />
        </section>

        {/* Hero Section */}
        <section 
          data-testid="hero-section"
          className="text-center mb-16 opacity-0 animate-fade-in-up animate-delay-100"
        >
          {/* Headline */}
          <h1 
            data-testid="headline"
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide mb-6 leading-tight uppercase"
            style={{ color: COLORS.navy }}
          >
            Travel Is Your Lifestyle — And That's Enough
          </h1>
          
          {/* Subhead */}
          <p 
            data-testid="subhead"
            className="text-base md:text-lg font-medium mb-12"
            style={{ color: COLORS.slateBlue }}
          >
            You don't need to turn everything you love into a business.
          </p>

          {/* Hero Image */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_travel-fulfillment/artifacts/q9ffnq2z_IMG_8304.jpeg" 
              alt="Peaceful travel scenery" 
              className="hero-image"
              data-testid="hero-image"
            />
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-200"></div>

        {/* Body Copy Section */}
        <section 
          data-testid="body-section"
          className="mb-16 opacity-0 animate-fade-in-up animate-delay-300"
        >
          <div className="text-center space-y-8">
            <p 
              data-testid="body-paragraph-1"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              You travel because you enjoy it.
              <br />
              The experience. The escape. The reward.
            </p>

            <p 
              data-testid="body-paragraph-2"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              This assessment wasn't designed to push you — only to reflect what fits you right now.
            </p>

            <p 
              data-testid="body-paragraph-3"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              For some people, travel stays exactly what it is:
              <br />
              A lifestyle.
              <br />
              A joy.
              <br />
              A personal investment in life.
            </p>

            <p 
              data-testid="body-paragraph-4"
              className="text-lg md:text-xl leading-relaxed font-medium"
              style={{ color: COLORS.charcoal }}
            >
              And that's a win.
            </p>

            <p 
              data-testid="body-paragraph-5"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              Others eventually explore opportunity through travel —
              <br />
              but only when they choose to.
            </p>

            <p 
              data-testid="body-paragraph-6"
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: COLORS.charcoal }}
            >
              There's no timeline.
              <br />
              No pressure.
              <br />
              No obligation.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="subtle-divider my-12 opacity-0 animate-fade-in-up animate-delay-400"></div>

        {/* CTA Section */}
        <section 
          data-testid="cta-section"
          className="text-center mb-20 opacity-0 animate-fade-in-up animate-delay-500"
        >
          <p 
            data-testid="cta-intro"
            className="text-base"
            style={{ color: COLORS.slateBlue }}
          >
            Curious what else is possible — someday?
            <br />
            You now know where to find it.
          </p>
        </section>

        {/* Footer / Contact Section */}
        <footer 
          data-testid="footer-section"
          className="text-center pt-8"
          style={{ borderTop: `1px solid ${COLORS.lightGray}` }}
        >
          <p 
            data-testid="footer-tagline"
            className="text-sm mb-2"
            style={{ color: COLORS.slateBlue }}
          >
            Stay connected with Turner Excursions
          </p>
          
          <p 
            data-testid="footer-mission"
            className="text-xs tracking-widest uppercase mb-6"
            style={{ color: COLORS.lightGray }}
          >
            Travel. Access. Opportunity.
          </p>

          {/* Email Signup Form */}
          <div 
            data-testid="email-signup-section"
            className="mb-8"
          >
            {!subscribed ? (
              <form 
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
                data-testid="email-signup-form"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full sm:w-auto flex-1 px-4 py-3 text-sm bg-white rounded focus:outline-none transition-colors duration-300"
                  style={{ 
                    color: COLORS.charcoal, 
                    border: `1px solid ${COLORS.lightGray}`,
                  }}
                  data-testid="email-input"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 py-3 text-xs font-medium uppercase tracking-widest bg-white rounded transition-colors duration-300 disabled:opacity-50"
                  style={{ 
                    color: COLORS.charcoal, 
                    border: `1px solid ${COLORS.lightGray}`,
                  }}
                  data-testid="subscribe-button"
                >
                  {isSubmitting ? "..." : "Stay Connected"}
                </button>
              </form>
            ) : (
              <p 
                data-testid="subscription-success"
                className="text-sm"
                style={{ color: COLORS.slateBlue }}
              >
                Thank you for connecting with us.
              </p>
            )}
          </div>

          <a 
            href={`mailto:${EMAIL}`}
            className="brand-link text-sm"
            data-testid="email-link"
          >
            {EMAIL}
          </a>
        </footer>

      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<QuizPage />} />
        <Route path="/quiz" element={<QuizPage />} />
        <Route path="/lifestyle" element={<LandingPage />} />
        <Route path="/connector" element={<ConnectorPage />} />
        <Route path="/strategic" element={<PatternPage />} />
        <Route path="/ownership" element={<OwnershipPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
